import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import SkipIcon from '@mui/icons-material/RemoveCircleOutline';

const stepIcon = (status) => {
  switch (status) {
    case 'completed':
    case 'done':
      return <CheckIcon sx={{ color: 'success.main' }} />;
    case 'running':
      return <CircularProgress size={20} />;
    case 'failed':
      return <ErrorIcon sx={{ color: 'error.main' }} />;
    case 'skipped':
      return <SkipIcon sx={{ color: 'grey.500' }} />;
    default:
      return <PendingIcon sx={{ color: 'grey.400' }} />;
  }
};

/**
 * Live / completed destroy progress for a tenant (Delete/Destroy in Progress → Deleted).
 */
const TenantDestroyProgressDialog = ({ open, onClose, tenant, progressPayload }) => {
  const progress = progressPayload?.progress || tenant?.destroy_progress || null;
  const steps = progress?.steps || [];
  const status = progressPayload?.status || tenant?.status;
  const error = progressPayload?.destroy_error || progress?.error || tenant?.destroy_error;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Destroy progress
        {tenant?.tenant_name ? ` — ${tenant.tenant_name}` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={status === 'deleted' ? 'default' : status === 'destroy_failed' ? 'error' : 'warning'}
            label={
              status === 'destroying'
                ? 'Delete/Destroy in Progress'
                : status === 'deleted'
                  ? 'Deleted'
                  : status === 'destroy_failed'
                    ? 'Destroy Failed'
                    : String(status || '')
            }
          />
          {progressPayload?.backup_file_path && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Backup: {progressPayload.backup_file_path}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {status === 'destroying' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Teardown is running. This dialog refreshes while open. The tenant stays in the list.
          </Alert>
        )}

        {steps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No progress details yet.</Typography>
        ) : (
          <List dense>
            {steps.map((s) => (
              <ListItem key={s.key} alignItems="flex-start">
                <ListItemIcon sx={{ minWidth: 36 }}>{stepIcon(s.status)}</ListItemIcon>
                <ListItemText
                  primary={`Step ${s.step}: ${s.title}`}
                  secondary={[s.message, s.detail, s.error].filter(Boolean).join(' — ') || s.description}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDestroyProgressDialog;
